import React, { useCallback, useEffect, useState } from 'react';
import { t, Trans } from '@lingui/macro';

import KEYS from '@rover/constants/src/platform/keys.constants';
import CarouselLeft from '@rover/icons/svg/carousel-left.svg';
import CarouselRight from '@rover/icons/svg/carousel-right.svg';
import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex, Paragraph, Text } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import useTheme from '@rover/kibble/themes/useTheme';
import { isReactNative } from '@rover/rsdk/src/modules/Network/userAgent';

import { useImageGallery } from './hooks/useImageGallery';
import { MAX_LIGHTBOX_IMAGE_HEIGHT, MAX_LIGHTBOX_IMAGE_WIDTH } from './constants';
import type { GalleryImage, PaginationConfig } from './ImageGallery.common';
import ImageWithShimmer from './ImageWithShimmer';
import { shouldLoadMore } from './utils';

type LightboxProps = {
  images: GalleryImage[] | undefined;
  lightboxIndex: number;
  setLightboxIndex: React.Dispatch<React.SetStateAction<number>>;
  onClose: () => void;
  onViewGallery: () => void;
  pagination?: PaginationConfig;
};

const Lightbox = ({
  images,
  lightboxIndex,
  setLightboxIndex,
  onClose,
  onViewGallery,
  pagination,
}: LightboxProps): JSX.Element | null => {
  const { colors } = useTheme();

  const {
    currentUri,
    currentSize,
    isCurrentImageFirst,
    isCurrentImageLast,
    hasNextImage,
    hasPrevImage,
    currentIndex,
  } = useImageGallery(images, lightboxIndex, { totalImageCount: pagination?.totalImageCount });

  const [cachedImages, setCachedImages] = useState<{ [k: string]: boolean }>({});

  const displayTotal = pagination?.totalImageCount ?? images?.length ?? 0;

  const showPrev = useCallback((): void => {
    if (isCurrentImageFirst) return;
    setLightboxIndex((prevIndex) => prevIndex - 1);
  }, [isCurrentImageFirst, setLightboxIndex]);

  const showNext = useCallback((): void => {
    if (isCurrentImageLast) return;
    if (shouldLoadMore(pagination) && lightboxIndex >= (images?.length ?? 0) - 2) {
      pagination?.onLoadMore();
    }
    setLightboxIndex((prevIndex) => prevIndex + 1);
  }, [isCurrentImageLast, setLightboxIndex, pagination, lightboxIndex, images]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === KEYS.LEFT) {
        showPrev();
      } else if (event.key === KEYS.RIGHT) {
        showNext();
      }
    },
    [showPrev, showNext]
  );

  useEffect(() => {
    if (process.env.JS_ENV_CLIENT) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    return () => {};
  }, [handleKeyDown]);

  if (!images) return null;

  const isWaitingForImages = lightboxIndex >= images.length;

  return (
    <Flex flex="1" alignItems="center" justifyContent="flex-start" flexDirection="column" gap="5x">
      <Flex justifyContent="space-between" flexDirection="row" width="100%" px={['4x', '0x']}>
        <Button onClick={onViewGallery} variant="default" contrast>
          {t`View gallery`}
        </Button>
        <Button
          variant="flat"
          icon={Close}
          onClick={onClose}
          aria-label={t`Dismiss`}
          sx={{
            color: colors.text.primary_inverse,
            '&:hover, &:focus': { color: colors.interactive_link.secondary_inverse_pressed },
          }}
          circular
          contrast
        />
      </Flex>
      <Flex
        flex="1"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        width="100%"
      >
        <Text size="100" textColor="primary_inverse" mb="4x">
          <Trans>
            {currentIndex + 1} of {displayTotal}
          </Trans>
        </Text>
        <Box position="relative" width="100%" textAlign="center">
          <Flex
            position={isReactNative() ? 'absolute' : 'fixed'}
            top="50%"
            left={['4x', '2x']}
            marginTop="-5x"
            zIndex="1"
          >
            <Button
              circular
              icon={CarouselLeft}
              aria-label={t`Previous`}
              disabled={!hasPrevImage}
              onPress={showPrev}
            />
          </Flex>
          <Flex
            width="100%"
            maxHeight="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            {isWaitingForImages ? (
              <LoadingShimmer
                width={MAX_LIGHTBOX_IMAGE_WIDTH}
                height={isReactNative() ? MAX_LIGHTBOX_IMAGE_HEIGHT : MAX_LIGHTBOX_IMAGE_WIDTH}
                borderRadius="primary"
                m="0x"
                data-testid="lightbox-loading-shimmer"
              />
            ) : (
              <ImageWithShimmer
                src={currentUri}
                caption={images[currentIndex]?.caption}
                width={currentSize.width || MAX_LIGHTBOX_IMAGE_WIDTH}
                height={
                  isReactNative()
                    ? MAX_LIGHTBOX_IMAGE_HEIGHT
                    : currentSize.height || MAX_LIGHTBOX_IMAGE_WIDTH
                }
                cached={cachedImages[currentUri]}
                setCached={(cached: boolean) => {
                  setCachedImages((prev) => ({ ...prev, [currentUri]: cached }));
                }}
              />
            )}
          </Flex>
          <Flex
            position={isReactNative() ? 'absolute' : 'fixed'}
            top="50%"
            right={['4x', '2x']}
            marginTop="-5x"
            zIndex="1"
          >
            <Button
              circular
              icon={CarouselRight}
              aria-label={t`Next`}
              disabled={!hasNextImage}
              onPress={showNext}
            />
          </Flex>
        </Box>
        {images[currentIndex]?.formattedAddedDate && (
          <Paragraph size="100" mt="20x" textColor="primary_inverse">
            <Trans>Uploaded {images[currentIndex].formattedAddedDate}</Trans>
          </Paragraph>
        )}
      </Flex>
    </Flex>
  );
};

export default Lightbox;
